<script setup lang="ts">
import BackComponent from '@/components/BackComponent.vue';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import FixedValueComponent from '@/components/FixedValueComponent.vue';
import NextComponent from '@/components/NextComponent.vue';
import SliderInputComponent from '@/components/SliderInputComponent.vue';
import { useAgreementStore } from '@/stores/agreement';
import { useConfigStore } from '@/stores/config';
import { calculateMaxYearlyMileage } from '@/helpers';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';

const agreementStore = useAgreementStore()
const { agreement, agreementType, calculation } = storeToRefs(useAgreementStore())
const { defaults } = storeToRefs(useConfigStore())

const valid = ref(false)

// When set on the agreement type, these lock the corresponding slider to a fixed value
// and take precedence over all max/total rules below.
const fixedDuration = computed(() => agreementType.value?.fixedDuration ?? null)
const fixedYearlyMileage = computed(() => agreementType.value?.fixedYearlyMileage ?? null)

const maxDuration = computed(() => {
  if (agreementType.value?.maxStartAge) {
    const age = new Date().getFullYear() - (agreement.value.registrationYear ?? new Date().getFullYear())
    return (agreementType.value?.maxDuration ?? defaults.value.maxDuration) - age
  }
  else {
    return agreementType.value?.maxDuration ?? defaults.value.maxDuration
  }
})

const maxYearlyMileage = computed(() => calculateMaxYearlyMileage(agreementType.value, defaults.value, agreement.value.oldMileage, agreement.value.duration))

watch(maxDuration, (newValue) => {
  // A fixed duration overrides every max rule — never clamp it.
  if (fixedDuration.value != null) return

  // If duration was undefined, set it to maximum allowed
  if (!agreement.value.duration || agreement.value.duration > newValue) {
    agreement.value.duration = newValue
  }
}, { immediate: true })

watch(maxYearlyMileage, (newValue) => {
  // A fixed yearly mileage overrides every max rule — never clamp it.
  if (fixedYearlyMileage.value != null) return

  if (agreement.value.yearlyMileage && agreement.value.yearlyMileage > newValue) {
    agreement.value.yearlyMileage = newValue
  }
}, { immediate: true })

onMounted(async () => {
  // Fixed values win over all the default/clamp logic below.
  if (fixedDuration.value != null) {
    agreement.value.duration = fixedDuration.value
  }

  if (fixedYearlyMileage.value != null) {
    agreement.value.yearlyMileage = fixedYearlyMileage.value
  } else {
    // if empty, set to 20k or max if lower than 20k
    if (!agreement.value.yearlyMileage) {
      if (maxYearlyMileage.value >= 20000)
        agreement.value.yearlyMileage = 20000
      else
        agreement.value.yearlyMileage = maxYearlyMileage.value
    }

    // if value is already higher, lower to max
    if (agreement.value.yearlyMileage && agreement.value.yearlyMileage > maxYearlyMileage.value)
      agreement.value.yearlyMileage = maxYearlyMileage.value
  }

  // if (!calculation.value) {
  //   agreementStore.calculate()
  // }

  agreementStore.calculate()
})
</script>

<template>
  <v-container>
    <v-form
      v-model="valid"
      ref="form"
    >
      <v-row>
        <v-col cols="12" md="6">
          <FixedValueComponent
            v-if="fixedDuration != null"
            :value="fixedDuration"
            unit="år"
            label="Hvor mange år skal aftalen løbe?"
          />
          <SliderInputComponent
            v-else-if="maxDuration"
            v-model="agreement.duration"
            unit="år"
            label="Hvor mange år skal aftalen løbe?"
            :max="maxDuration"
          />
        </v-col>
        <v-col>
          <FixedValueComponent
            v-if="fixedYearlyMileage != null"
            :value="fixedYearlyMileage"
            unit="km"
            label="Hvor mange kilometer kører du om året?"
          />
          <SliderInputComponent
            v-else-if="agreementType && agreement.duration && typeof agreement.oldMileage === 'number'"
            v-model="agreement.yearlyMileage"
            unit="km"
            label="Hvor mange kilometer kører du om året?"
            :min="5000"
            :max="maxYearlyMileage"
            :step="5000"
          />
        </v-col>
      </v-row>
    </v-form>
  </v-container>

  <BottomBarComponent>
    <BackComponent to="/agreement/type" />
    <NextComponent
      to="/agreement/payment"
      :disabled="!agreementStore.isOkay"
    />
  </BottomBarComponent>
</template>